import React, { useMemo, useState } from "react";
import Modal from "react-modal";
import * as XLSX from "xlsx";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../../../../css/RecordingOfScores.css";
import "../../../../css/Liveranking.css";
import TspLoadError from "../../components/common/TspLoadError";
import useTspApiResource from "../../hooks/useTspApiResource";
import TspApiService from "../../api/tspApiService";
import { CATEGORIES, COMPARE_ROWS, FILTER_BY, STATES, ZONES, buildView, compareColumns, display, downloadRows } from "./templeRankingModel";

const THIS_YEAR = new Date().getFullYear();
const NO_SEARCH = { name: "", category: "", zone: "" };
// The ICC Live Ranking's Your Stats bag icon.
function BagIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12.4744 17.0183H5.51784C2.96254 17.0183 1.0022 16.0954 1.55903 12.3807L2.20739 7.34633C2.55064 5.49279 3.73294 4.78341 4.77032 4.78341H13.2524C14.305 4.78341 15.4187 5.54618 15.8153 7.34633L16.4637 12.3807C16.9366 15.6759 15.0297 17.0183 12.4744 17.0183Z"
        stroke="#130F26"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M12.5889 4.60034C12.5889 2.61194 10.977 1.00003 8.98861 1.00003V1.00003C8.03111 0.995975 7.11143 1.3735 6.43294 2.04913C5.75445 2.72476 5.37304 3.64283 5.37305 4.60034H5.37305" stroke="#130F26" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M11.46 8.3532H11.4219" stroke="#130F26" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6.60114 8.3532H6.563" stroke="#130F26" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ResetIcon() {
  return (
    <svg width="14" height="18" viewBox="0 0 14 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M7.14969 3.72539V0.349762L2.93016 4.56929L7.14969 8.78883V5.4132C9.94302 5.4132 12.2131 7.68331 12.2131 10.4766C12.2131 13.27 9.94302 15.5401 7.14969 15.5401C4.35636 15.5401 2.08625 13.27 2.08625 10.4766H0.398438C0.398438 14.2067 3.41962 17.2279 7.14969 17.2279C10.8798 17.2279 13.9009 14.2067 13.9009 10.4766C13.9009 6.74657 10.8798 3.72539 7.14969 3.72539Z"
        fill="#9F1D21"
      />
    </svg>
  );
}

function saveSheet(rows, sheetName, fileName) {
  const sheet = XLSX.utils.json_to_sheet(rows);
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, sheetName);
  XLSX.writeFile(book, fileName);
}

// TSP Live Ranking — the ICC Live Ranking screen (src/pages/LiveRanking.jsx:
// same cards, filter bar, tables and Compare), for temples ranked by the
// NUMBER OF YATRAS they ran in the year (not books). Filters: year, then All
// India / Zone / Category / State with ICC's own zone, category and state
// lists; ICC's event filter ("Overall Book Distribution") and Book Wise are not
// here. Data: GET /tsp/ranking/temples?year= (templeRanking.js on the server).
export default function TspLiveRankingPage() {
  const [year, setYear] = useState(THIS_YEAR);
  const [filterBy, setFilterBy] = useState("");
  const [zone, setZone] = useState("");
  const [category, setCategory] = useState("");
  const [state, setState] = useState("");
  const [searchOpen, setSearchOpen] = useState(null); // "name" | "category" | "zone" | null
  const [search, setSearch] = useState(NO_SEARCH);
  const [compareOpen, setCompareOpen] = useState(false);

  const ranking = useTspApiResource(() => TspApiService.getTempleRanking(year), [year], { refreshMs: 30000 });
  const data = ranking.data;
  const temples = useMemo(() => (data ? data.temples : []), [data]);
  const view = useMemo(() => buildView(temples, { filterBy, zone, category, state, search }), [temples, filterBy, zone, category, state, search]);
  const mine = data ? data.mine : null;

  const resetFilters = () => {
    setYear(THIS_YEAR);
    setFilterBy("");
    setZone("");
    setCategory("");
    setState("");
    setSearch(NO_SEARCH);
    setSearchOpen(null);
  };

  const changeFilterBy = (value) => {
    setFilterBy(value);
    setZone("");
    setCategory("");
    setState("");
  };

  const download = () => saveSheet(downloadRows(view), "Live Ranking", `TSP-Live-Ranking-${year}.xlsx`);

  const searchHeader = (key, label) => (
    <th style={{ textAlign: "left", cursor: "pointer" }} className="table-row-header" onClick={() => setSearchOpen(searchOpen === key ? null : key)} aria-expanded={searchOpen === key}>
      {label}
      <span style={{ marginLeft: "5px", fontWeight: "normal", fontSize: "18px" }}>{searchOpen === key ? <i className="bi bi-chevron-up" /> : <i className="bi bi-chevron-down" />}</span>
    </th>
  );

  const renderTable = () => {
    if (ranking.error) return <TspLoadError error={ranking.error} onRetry={ranking.reload} what="the live ranking" />;
    if (!data) return <p className="filter-text text-center mt-4">Loading ranking…</p>;

    if (view.kind === "groups") {
      return (
        <table className="table custom-table">
          <thead>
            <tr>
              <th style={{ textAlign: "center" }} className="table-row-header">
                Ranking
              </th>
              <th style={{ textAlign: "left" }} className="table-row-header">
                {view.label}
              </th>
              <th style={{ textAlign: "right" }} className="table-row-header">
                Temples
              </th>
              <th style={{ textAlign: "right" }} className="table-row-header">
                Number of Yatras
              </th>
            </tr>
          </thead>
          <tbody>
            {view.rows.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-center">
                  No yatras in {year} yet.
                </td>
              </tr>
            ) : (
              view.rows.map((row) => (
                <tr key={row.name}>
                  <td style={{ textAlign: "center" }}>{row.rank}</td>
                  <td style={{ textAlign: "left" }}>{row.name}</td>
                  <td style={{ textAlign: "right" }}>{display(row.temples)}</td>
                  <td style={{ textAlign: "right" }}>{display(row.yatras)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      );
    }

    return (
      <table className="table custom-table">
        <thead>
          <tr>
            <th style={{ textAlign: "center" }} className="table-row-header">
              Ranking
            </th>
            {searchHeader("name", "Particular")}
            {searchHeader("category", "Category")}
            {searchHeader("zone", "Zone")}
            <th style={{ textAlign: "right" }} className="table-row-header">
              Number of Yatras
            </th>
          </tr>
          {searchOpen && (
            <tr>
              <th colSpan={5}>
                <input
                  type="search"
                  className="form-control"
                  placeholder={`Search ${searchOpen === "name" ? "temple" : searchOpen}`}
                  aria-label={`Search ${searchOpen === "name" ? "temple" : searchOpen}`}
                  value={search[searchOpen]}
                  onChange={(event) => setSearch({ ...search, [searchOpen]: event.target.value })}
                  autoFocus
                />
              </th>
            </tr>
          )}
        </thead>
        <tbody>
          {view.rows.length === 0 ? (
            <tr>
              <td colSpan={5} className="text-center">
                No yatras in {year} yet.
              </td>
            </tr>
          ) : (
            view.rows.map((row) => (
              <tr key={row.temple_name} className={mine && row.temple_name === mine.temple_name ? "tsp-live-ranking-mine" : undefined} aria-current={mine && row.temple_name === mine.temple_name ? "true" : undefined}>
                <td style={{ textAlign: "center" }}>{row.rank}</td>
                <td style={{ textAlign: "left" }}>{row.temple_name}</td>
                <td style={{ textAlign: "left" }}>{display(row.category)}</td>
                <td style={{ textAlign: "left" }}>{display(row.zone)}</td>
                <td style={{ textAlign: "right" }}>{display(row.yatras)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    );
  };

  return (
    <div className="container common-container update-responsive live-ranking live-ranking-table tsp-live-ranking">
      <div className="update-score-container">
        <div className="row">
          <div className={mine ? "col-12 col-md-5" : "col-12"}>
            <div className="card card-shadow tsp-live-ranking-card">
              <div className="card-body">
                <h5 className="card-title">Live Ranking</h5>
                <p className="card-text">Track real-time rankings of temples by the number of Travelling Sankirtan yatras they run.</p>
              </div>
            </div>
          </div>
          {mine && (
            <div className="col-12 col-md-7">
              <div className="card card-shadow tsp-live-ranking-card">
                <div className="card-body">
                  <h5 className="stats-header">
                    <BagIcon />
                    &nbsp; Your Stats <span className="tsp-live-ranking-temple">· {mine.temple_name}</span>
                  </h5>
                  <div className="card-text stats">
                    <div className="text-center">
                      <p className="ranking-status-text">All India Rank</p>
                      <span className="ranking-count">{display(mine.all_rank)}</span>
                    </div>
                    <div className="text-center">
                      <p className="ranking-status-text">State Rank</p>
                      <span className="ranking-count">{display(mine.state_rank)}</span>
                    </div>
                    <div className="text-center">
                      <p className="ranking-status-text">Zonal Rank</p>
                      <span className="ranking-count">{display(mine.zone_rank)}</span>
                    </div>
                    <div className="text-center">
                      <p className="ranking-status-text">Category Rank</p>
                      <span className="ranking-count">{display(mine.category_rank)}</span>
                    </div>
                    <div className="text-center">
                      <p className="ranking-status-text">Number of Yatras</p>
                      <span className="ranking-count">{display(mine.yatras)}</span>
                    </div>
                    <div className="text-center">
                      <p className="ranking-status-text">Completed Yatras</p>
                      <span className="ranking-count">{display(mine.completed)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Filters (the ICC Live Ranking filter bar) */}
        <div className="container">
          <div className="row mt-4 filters-wrap">
            <div className="col-12 col-md-10 filter-container p-0 event-filter">
              <button
                type="button"
                className="tsp-live-ranking-plain-btn"
                style={{ width: "100px", display: "flex", justifyContent: "space-around", alignItems: "center", textAlign: "center", cursor: "pointer" }}
                onClick={download}
                disabled={!data}
              >
                Download
              </button>
              <span className="filter-border"></span>
              <p className="filter-text fordrop-down" style={{ width: "100px", display: "flex", justifyContent: "space-around", alignItems: "center" }}>
                Filter By
              </p>
              <span className="filter-border"></span>
              <select className="form-select text-center" value={year} onChange={(event) => setYear(Number(event.target.value))} aria-label="Year">
                {(data ? data.years : [year]).map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <span className="filter-border"></span>
              <select className="form-select text-center" value={filterBy} onChange={(event) => changeFilterBy(event.target.value)} aria-label="Filter By">
                {FILTER_BY.map((option) => (
                  <option key={option.value} value={option.value} className="filter-text">
                    {option.label}
                  </option>
                ))}
              </select>
              <span className="filter-border"></span>

              {filterBy === "zone" && (
                <>
                  <select className="form-select" value={zone} onChange={(event) => setZone(event.target.value)} aria-label="Zone">
                    <option value="" className="filter-text text-center">
                      Select Zone
                    </option>
                    {ZONES.map((option) => (
                      <option key={option} value={option} className="filter-text text-center">
                        {option}
                      </option>
                    ))}
                  </select>
                  <span className="filter-border"></span>
                  {zone && (
                    <>
                      <select className="form-select" value={category} onChange={(event) => setCategory(event.target.value)} aria-label="Category">
                        <option value="" className="filter-text">
                          Select Category
                        </option>
                        {CATEGORIES.map((option) => (
                          <option key={option} value={option} className="filter-text">
                            {option}
                          </option>
                        ))}
                      </select>
                      <span className="filter-border"></span>
                    </>
                  )}
                </>
              )}

              {filterBy === "category" && (
                <>
                  <select className="form-select" value={category} onChange={(event) => setCategory(event.target.value)} aria-label="Category">
                    <option value="" className="filter-text">
                      Select Category
                    </option>
                    {CATEGORIES.map((option) => (
                      <option key={option} value={option} className="filter-text">
                        {option}
                      </option>
                    ))}
                  </select>
                  <span className="filter-border"></span>
                  {category && (
                    <>
                      <select className="form-select" value={zone} onChange={(event) => setZone(event.target.value)} aria-label="Zone">
                        <option value="" className="filter-text text-center">
                          Select Zone
                        </option>
                        {ZONES.map((option) => (
                          <option key={option} value={option} className="filter-text text-center">
                            {option}
                          </option>
                        ))}
                      </select>
                      <span className="filter-border"></span>
                    </>
                  )}
                </>
              )}

              {filterBy === "state" && (
                <>
                  <select className="form-select" value={state} onChange={(event) => setState(event.target.value)} aria-label="State">
                    <option value="" className="filter-text">
                      Select State
                    </option>
                    {STATES.map((option) => (
                      <option key={option} value={option} className="filter-text">
                        {option}
                      </option>
                    ))}
                  </select>
                  <span className="filter-border"></span>
                </>
              )}

              <button type="button" className="d-flex filter-by tsp-live-ranking-plain-btn" style={{ textAlign: "center", cursor: "pointer", paddingLeft: "8px", alignItems: "center" }} onClick={resetFilters}>
                <ResetIcon />
                &nbsp;&nbsp;
                <span className="filter-text reset-live-filters">Reset Filter</span>
              </button>

              {/* Phone view */}
              <button type="button" className="d-flex align-items-center justify-content-center filter-by d-md-none tsp-live-ranking-plain-btn" style={{ cursor: "pointer", padding: "8px" }} onClick={() => setCompareOpen(true)}>
                <span className="filter-text reset-live-filters ms-2">Compare</span>
              </button>
            </div>

            <div className="col-12 col-md-2 d-flex align-items-center justify-content-end p-0 d-none d-md-flex">
              <button type="button" className="custom-compare-btn" onClick={() => setCompareOpen(true)}>
                Compare
              </button>
            </div>
          </div>
        </div>

        {/* Live Ranking table */}
        <div className="container mt-4">
          <div className="tsp-live-ranking-table-wrap">{renderTable()}</div>
        </div>

        {compareOpen && <CompareModal initialYear={year} years={data ? data.years : [year]} mine={mine} onClose={() => setCompareOpen(false)} />}
      </div>
    </div>
  );
}

// The ICC compare dialog's look (src/pages/LiveRanking.jsx).
const COMPARE_MODAL_STYLE = {
  content: { position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", padding: "25px", borderRadius: "10px", maxWidth: "85%", maxHeight: "90%", width: "1363px", height: "100%" },
  overlay: { backgroundColor: "rgba(0, 0, 0, 0.5)", backdropFilter: "blur(3px)", zIndex: 1000 },
};
const CLOSE_BUTTON_STYLE = {
  position: "absolute",
  top: "0px",
  right: "0px",
  border: "none",
  background: "#9F1D21",
  borderRadius: "50%",
  width: "30px",
  height: "30px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "18px",
  fontWeight: "bold",
  cursor: "pointer",
  color: "#fff",
  lineHeight: "1",
};

// "Compare With Another Temple" — up to four temples side by side for a year
// (the ICC compare dialog), the first being the caller's own temple.
function CompareModal({ initialYear, years, mine, onClose }) {
  const [year, setYear] = useState(initialYear);
  const [selected, setSelected] = useState([mine ? mine.temple_name : "", "", "", ""]);
  const ranking = useTspApiResource(() => TspApiService.getTempleRanking(year), [year]);
  const temples = useMemo(() => {
    const list = ranking.data ? ranking.data.temples : [];
    const own = ranking.data && ranking.data.mine;
    // The caller's own temple can be compared even before its first yatra.
    return own && !list.some((row) => row.temple_name === own.temple_name) ? [...list, own] : list;
  }, [ranking.data]);
  const columns = compareColumns(temples, selected);

  const downloadCompare = () =>
    saveSheet(
      COMPARE_ROWS.map((row) => Object.fromEntries([["", row.label], ...columns.map((column, index) => [`Temple ${index + 1}`, column ? display(row.value(column)) : ""])])),
      "Compare",
      `TSP-Live-Ranking-Compare-${year}.xlsx`
    );

  return (
    <Modal
      isOpen
      onRequestClose={onClose}
      contentLabel="Compare Temples"
      className="Modal compare-modal"
      overlayClassName="Overlay"
      ariaHideApp={false}
      style={COMPARE_MODAL_STYLE}
    >
      <div className="compare-table-body">
        <div className="col-12 text-right">
          <button type="button" style={CLOSE_BUTTON_STYLE} onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>
        <div className="row align-items-start" style={{ marginBottom: "16px", marginTop: "20px", padding: "0px 5px" }}>
          <div className="col-12 col-md-5 text-start">
            <h5 className="filter-text" style={{ fontSize: "20px", fontWeight: 600 }}>
              Compare With Another Temple
            </h5>
          </div>
          <div className="col-12 col-md-3 text-right">
            <button type="button" className="custom-compare-btn" onClick={downloadCompare}>
              Download Excel
            </button>
          </div>
          <div className="col-12 col-md-4 text-right">
            <select className="form-select text-start filter-text" value={year} onChange={(event) => setYear(Number(event.target.value))} aria-label="Compare year">
              {years.map((option) => (
                <option key={option} value={option} className="filter-text">
                  {option}
                </option>
              ))}
            </select>
          </div>
        </div>

        {ranking.error ? (
          <TspLoadError error={ranking.error} onRetry={ranking.reload} what="the ranking" />
        ) : (
          <div className="table-responsive">
            <table className="table table-striped table-bordered mt-2 custom-comparetable">
              <thead>
                <tr>
                  <th />
                  {selected.map((name, index) => (
                    <th key={index}>
                      <select
                        className="form-select text-center filter-text"
                        value={name}
                        aria-label={`Temple ${index + 1}`}
                        onChange={(event) => setSelected(selected.map((current, i) => (i === index ? event.target.value : current)))}
                      >
                        <option value="">Select Temple</option>
                        {temples.map((temple) => (
                          <option key={temple.temple_name} value={temple.temple_name}>
                            {temple.temple_name}
                          </option>
                        ))}
                      </select>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARE_ROWS.map((row) => (
                  <tr key={row.label}>
                    <td className="text-start">
                      <span className="filter-text" style={{ fontSize: "16px" }}>
                        {row.label}
                      </span>
                    </td>
                    {columns.map((column, index) => (
                      <td key={index} className="text-center">
                        {column ? display(row.value(column)) : ""}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}
